import { useCallback, useEffect, useRef, useState } from 'react';
import {
  loadCheckoutWebComponents,
  StoredCardDisplayMode,
  type CheckoutWebComponents,
  type Component,
  type EnvironmentUnion,
  type PartialDesignTokens,
  type CheckoutError,
} from '@checkout.com/checkout-web-components';
import { logError } from 'components/LogError';
import { useCreateCheckoutDotComPaymentSession } from './useCreateCheckoutDotComPaymentSession';
import { useFinalizeCheckoutDotComPaymentSession } from './useFinalizeCheckoutDotComPaymentSession';

/** checkout.com sandbox public keys are prefixed `pk_sbox_`; live keys are `pk_`. */
const resolveEnvironment = (publicKey: string): EnvironmentUnion =>
  publicKey.startsWith('pk_sbox_') ? 'sandbox' : 'production';

interface UseCheckoutDotComFlowCardArgs {
  /**
   * `card` renders empty card fields for a NEW card; `stored_card` renders ONLY the customer's saved
   * card(s). We deliberately use the dedicated `stored_card` component rather than the generic `flow`
   * aggregator, which would also surface every other enabled method (Apple Pay / Google Pay) — mirrors
   * cultx-web's `StoredCardFlowForm`.
   */
  componentType?: 'card' | 'stored_card';
  amount: number;
  successUrl: string;
  failureUrl: string;
  /** checkout.com rejects the session without a customer name and email — source these from the authed user. */
  customerName?: string;
  customerEmail?: string;
  billingCountry?: string;
  portfolioId?: number;
  referenceId?: string;
  operationType?: string;
  transactionType?: string;
  currency?: string;
  /** Merchant name shown to the customer; checkout.com rejects the session with [display_name_invalid] when missing. */
  displayName?: string;
  // Full Flow session values, captured BEFORE session creation (the session is immutable once created).
  /** Cardholder name baked into the session. */
  clientName?: string;
  storeCardFlag?: boolean;
  mitConsentedFlag?: boolean;
  defaultMitFlag?: boolean;
  /** Stored-card charge: the backend configures checkout.com's `stored_card` from this so the SDK renders the saved card. */
  instrumentId?: string | null;
  /** Hold off creating the session / mounting the SDK until the form has captured the final values (phase 2). */
  enabled?: boolean;
  /** Called when the SDK reports a submit error / decline (no redirect), so the form can reset its state. */
  onError?: () => void;
}

interface UseCheckoutDotComFlowCardResult {
  /** Mount point for the SDK-rendered card / saved-card UI. */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Submit the charge against the session (Full Flow). The SDK completes it synchronously, via 3DS redirect, or errors. */
  submit: () => void;
  /** True once the card fields are valid (always true for the `stored_card` component once ready). */
  isCardComplete: boolean;
  /** True once the SDK component has mounted and is ready for input. */
  ready: boolean;
  /** True if session creation or SDK init failed. */
  error: boolean;
}

/**
 * checkout.com Flow migration — FULL FLOW strategy. Reusable across the portal's card-capture forms.
 *
 * Unlike the earlier tokenize approach, the Flow SDK submits the charge ITSELF against the
 * backend-created payment session — there is NO `createCardPayment` call. Once `enabled` (the form's
 * phase-2 gate) and the customer details resolve, we create the session with the FINAL values
 * (cardholder name, store-card / MIT / default consents, and `instrumentId` for a stored card),
 * initialise the SDK, and mount the `card` (new) or `stored_card` (saved) component with `showPayButton:false`
 * so the form's own button drives `submit()`.
 *
 * On a synchronous (non-3DS) approval the SDK fires `onPaymentCompleted`: we `finalizeSession(pay_id)`
 * so the backend stores the card immediately, then navigate to the session's `successUrl`. The 3DS path
 * instead redirects the browser to `successUrl` / `failureUrl` (finalized by `useFinalizeOnPaymentRedirect`).
 */
export function useCheckoutDotComFlowCard({
  componentType = 'card',
  amount,
  successUrl,
  failureUrl,
  customerName,
  customerEmail,
  billingCountry,
  portfolioId,
  referenceId,
  operationType,
  transactionType,
  currency = 'GBP',
  displayName = 'Cult Wines',
  clientName,
  storeCardFlag,
  mitConsentedFlag,
  defaultMitFlag,
  instrumentId,
  enabled = true,
  onError,
}: UseCheckoutDotComFlowCardArgs): UseCheckoutDotComFlowCardResult {
  const { createSession } = useCreateCheckoutDotComPaymentSession();
  const { finalizeSession } = useFinalizeCheckoutDotComPaymentSession();

  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const [isCardComplete, setIsCardComplete] = useState(false);

  // The Flow SDK instance and the mounted component. Held in refs so they survive re-renders and are
  // reachable from `submit()` / cleanup without re-initialising.
  const checkoutRef = useRef<CheckoutWebComponents | null>(null);
  const componentRef = useRef<Component | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Guards so session creation and SDK init each run exactly once per enablement (re-renders / StrictMode).
  const sessionRequestedRef = useRef(false);
  const sdkInitRef = useRef(false);
  // Tracks the amount the current session was created with, to detect upstream amount edits.
  const prevAmountRef = useRef(amount);
  // Latest onError so the SDK callback (created once) always calls the current handler.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const publicKey = process.env.REACT_APP_CHECKOUT_COM_PUBLIC_KEY ?? '';

  const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  const teardown = useCallback(() => {
    componentRef.current?.unmount();
    componentRef.current = null;
    checkoutRef.current = null;
    sessionRequestedRef.current = false;
    sdkInitRef.current = false;
    setReady(false);
    setError(false);
    setIsCardComplete(false);
  }, []);

  useEffect(() => {
    if (sessionRequestedRef.current || !enabled) {
      return;
    }
    // checkout.com rejects the session if the customer name / email are missing, so wait for them.
    if (!customerName || !customerEmail || !successUrl || !failureUrl) {
      return;
    }
    sessionRequestedRef.current = true;
    prevAmountRef.current = amount;

    (async () => {
      const session = await createSession({
        amount,
        currency,
        displayName,
        successUrl,
        failureUrl,
        customerName,
        customerEmail,
        clientName,
        storeCardFlag,
        mitConsentedFlag,
        defaultMitFlag,
        ...(billingCountry ? { billingCountry } : {}),
        ...(instrumentId ? { instrumentId } : {}),
        ...(portfolioId ? { portfolioId } : {}),
        ...(referenceId ? { referenceId } : {}),
        ...(operationType ? { operationType } : {}),
        ...(transactionType ? { transactionType } : {}),
      });
      if (!session) {
        setError(true);
        return;
      }

      try {
        const appearance: PartialDesignTokens = {
          colorAction: isDarkMode ? '#14b8a6' : '#f97316',
          colorPrimary: isDarkMode ? '#f8fafc' : '#1f2937',
          colorBackground: isDarkMode ? '#334155' : '#ffffff',
          colorBorder: isDarkMode ? '#475569' : '#d1d5db',
          colorError: '#ef4444',
        };
        const checkout = await loadCheckoutWebComponents({
          publicKey,
          environment: resolveEnvironment(publicKey),
          // The SDK types `paymentSession` as a loose `Record<string, unknown>`; we pass the
          // `{ id, payment_session_token }` shape it actually requires.
          paymentSession: session as unknown as Record<string, unknown>,
          appearance,
        });
        checkoutRef.current = checkout;

        if (sdkInitRef.current || !containerRef.current) {
          return;
        }
        sdkInitRef.current = true;

        const component = checkout.create(componentType, {
          showPayButton: false,
          // Only the `card` component takes a cardholder name input (we hide it — the form collects it).
          ...(componentType === 'card' ? { displayCardholderName: 'hidden' as const } : {}),
          // Show every saved card so the user can still change cards here; `default_only` (the SDK
          // default) would collapse the list to a single card.
          ...(componentType === 'stored_card' ? { displayMode: StoredCardDisplayMode.All } : {}),
          onReady: () => {
            setReady(true);
            // The `stored_card` component has no validity event; it's ready to submit once mounted.
            if (componentType === 'stored_card') {
              setIsCardComplete(true);
            }
          },
          onChange: (c: Component) => setIsCardComplete(c.isValid()),
          // Synchronous approval (no 3DS). The SDK does NOT redirect here, so we navigate to the session's
          // success URL ourselves. Before navigating we finalize so the backend stores the card / credits
          // the wallet immediately rather than waiting on the slow checkout.com webhook. Best-effort: a
          // finalize failure must NOT block the success navigation.
          onPaymentCompleted: async (_c: Component, payment: { id: string }) => {
            await finalizeSession(payment.id);
            window.location.href = successUrl;
          },
          onError: (_c: Component, err: CheckoutError) => {
            logError(new Error(`Flow payment error [${(err as { code?: string })?.code ?? 'unknown'}]`), {
              tags: { userFlow: 'payment' },
            });
            onErrorRef.current?.();
          },
        });
        componentRef.current = component;
        // The component requires `isAvailable()` to be called before `mount()` — calling `mount()` first
        // throws `component_is_available_not_checked`.
        const available = await component.isAvailable();
        if (!available) {
          logError(new Error(`Flow ${componentType} component is not available on this device`), {
            tags: { userFlow: 'payment' },
          });
          setError(true);
          return;
        }
        component.mount(containerRef.current);
      } catch (e) {
        const err = e as { type?: string; code?: string; message?: string };
        logError(
          new Error(`Flow init failed [${err?.type ?? 'unknown'}/${err?.code ?? 'unknown'}]: ${err?.message ?? ''}`),
          { tags: { userFlow: 'payment' } },
        );
        setError(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, customerName, customerEmail, successUrl, failureUrl]);

  // The session amount is fixed at creation. If the upstream amount changes after the session was
  // created, tear down so it is recreated for the new amount on the next enabled render.
  useEffect(() => {
    if (prevAmountRef.current === amount) {
      return;
    }
    prevAmountRef.current = amount;
    if (sdkInitRef.current) {
      teardown();
    }
  }, [amount, teardown]);

  // Unmount the Flow component when the consuming form goes away.
  useEffect(
    () => () => {
      componentRef.current?.unmount();
      componentRef.current = null;
    },
    [],
  );

  const submit = useCallback(() => {
    try {
      componentRef.current?.submit();
    } catch (e) {
      logError(e as Error, { tags: { userFlow: 'payment' } });
      onErrorRef.current?.();
    }
  }, []);

  return { containerRef, submit, isCardComplete, ready, error };
}
